import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import BackToTop from '@/components/feature/BackToTop';
import { articles, categories, getFeaturedArticles } from '@/mocks/articles';

export default function Article() {
  const { id } = useParams<{ id: string }>();
  const article = articles.find(a => a.id === Number(id));

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="pt-16 min-h-[60vh] flex items-center justify-center">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
              <i className="ri-file-unknow-line text-3xl text-foreground-400"></i>
            </div>
            <h1 className="text-xl font-heading font-bold text-foreground-900 mb-2">文章未找到</h1>
            <p className="text-foreground-600 mb-6">该文章可能已被移除或链接失效</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              <i className="ri-arrow-left-line"></i>
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const category = categories.find(c => c.slug === article.category);
  const relatedArticles = articles
    .filter(a => a.category === article.category && a.id !== article.id)
    .slice(0, 3);

  return (
    <>
      <Navbar />
      <main className="pt-16">
        <article>
          {/* Article Header */}
          <header className="bg-background-100 py-10 md:py-14">
            <div className="max-w-[800px] mx-auto px-4 md:px-6">
              {/* Breadcrumb */}
              <nav className="flex items-center gap-2 text-xs md:text-sm text-foreground-500 mb-6 flex-wrap" aria-label="面包屑导航">
                <Link to="/" className="hover:text-primary-600 transition-colors whitespace-nowrap">首页</Link>
                <i className="ri-arrow-right-s-line text-xs"></i>
                <Link to={`/category/${article.category}`} className="hover:text-primary-600 transition-colors whitespace-nowrap">
                  {category?.name}
                </Link>
                <i className="ri-arrow-right-s-line text-xs"></i>
                <span className="text-foreground-800 font-medium line-clamp-1">{article.title}</span>
              </nav>

              <div className="flex items-center gap-2 mb-4 flex-wrap">
                <span className="inline-block px-2.5 py-1 rounded-full text-xs font-medium bg-primary-500 text-background-50 whitespace-nowrap">
                  {category?.name}
                </span>
                {article.tags.map(tag => (
                  <span key={tag} className="inline-block px-2.5 py-1 rounded-full text-xs bg-accent-100 text-accent-700 whitespace-nowrap">
                    {tag}
                  </span>
                ))}
              </div>

              <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-4 leading-tight">
                {article.title}
              </h1>

              <p className="text-sm md:text-base text-foreground-600 mb-6">
                {article.excerpt}
              </p>

              <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0">
                  <img src={article.authorAvatar} alt={article.author} className="w-full h-full object-cover" />
                </div>
                <div>
                  <p className="text-sm font-medium text-foreground-900">{article.author}</p>
                  <div className="flex items-center gap-3 text-xs text-foreground-500">
                    <span>{article.publishDate}</span>
                    <span>{article.readTime} 阅读</span>
                    <span className="flex items-center gap-1">
                      <i className="ri-eye-line"></i>
                      {article.views.toLocaleString()}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </header>

          {/* Featured Image */}
          <div className="max-w-[800px] mx-auto px-4 md:px-6 -mt-6">
            <div className="rounded-xl overflow-hidden">
              <img
                src={article.coverImage}
                alt={article.title}
                title={`${article.title} - 尤物视频`}
                className="w-full aspect-[16/9] object-cover object-top"
              />
            </div>
          </div>

          {/* Article Content */}
          <section className="py-10 md:py-14">
            <div
              className="max-w-[800px] mx-auto px-4 md:px-6 prose-custom"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />
          </section>

          {/* Article Footer */}
          <div className="max-w-[800px] mx-auto px-4 md:px-6 pb-10">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 py-6 border-t border-background-200/60">
              <div className="flex items-center gap-3">
                <button className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-background-100 text-foreground-700 hover:bg-primary-50 hover:text-primary-600 text-sm transition-colors cursor-pointer whitespace-nowrap">
                  <i className="ri-heart-line"></i>
                  喜欢 ({article.likes.toLocaleString()})
                </button>
                <button className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-background-100 text-foreground-700 hover:bg-accent-50 hover:text-accent-600 text-sm transition-colors cursor-pointer whitespace-nowrap">
                  <i className="ri-bookmark-line"></i>
                  收藏
                </button>
                <button className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-background-100 text-foreground-700 hover:bg-secondary-50 hover:text-secondary-600 text-sm transition-colors cursor-pointer whitespace-nowrap">
                  <i className="ri-share-forward-line"></i>
                  分享
                </button>
              </div>
            </div>

            {/* Author Card */}
            <div className="flex items-center gap-4 p-5 rounded-xl bg-background-100 mt-4">
              <div className="w-14 h-14 rounded-full overflow-hidden flex-shrink-0">
                <img src={article.authorAvatar} alt={article.author} className="w-full h-full object-cover" />
              </div>
              <div>
                <p className="text-sm font-medium text-foreground-900">{article.author}</p>
                <p className="text-xs text-foreground-600">尤物视频签约作者</p>
              </div>
            </div>
          </div>
        </article>

        {/* Related Articles */}
        {relatedArticles.length > 0 && (
          <section className="py-10 md:py-14 bg-background-100">
            <div className="max-w-[1280px] mx-auto px-4 md:px-6">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-8">
                相关推荐
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {relatedArticles.map((related) => (
                  <Link
                    key={related.id}
                    to={`/article/${related.id}`}
                    className="group bg-background-50 rounded-xl overflow-hidden hover:scale-[1.02] transition-all duration-300 cursor-pointer"
                  >
                    <div className="relative aspect-[16/9] overflow-hidden">
                      <img
                        src={related.coverImage}
                        alt={related.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-4">
                      <h3 className="text-sm font-heading font-semibold text-foreground-900 mb-2 line-clamp-2 group-hover:text-primary-600 transition-colors">
                        {related.title}
                      </h3>
                      <div className="flex items-center gap-3 text-xs text-foreground-500">
                        <span>{related.publishDate}</span>
                        <span>{related.readTime} 阅读</span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
      <Footer />
      <BackToTop />

      {/* JSON-LD */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: article.title,
            description: article.excerpt,
            image: article.coverImage,
            author: {
              '@type': 'Person',
              name: article.author,
            },
            datePublished: article.publishDate,
            publisher: {
              '@type': 'Organization',
              name: '尤物视频',
            },
            mainEntityOfPage: {
              '@type': 'WebPage',
              '@id': `https://c778v.cn/article/${article.id}`,
            },
            breadcrumb: {
              '@type': 'BreadcrumbList',
              itemListElement: [
                {
                  '@type': 'ListItem',
                  position: 1,
                  name: '首页',
                  item: 'https://c778v.cn',
                },
                {
                  '@type': 'ListItem',
                  position: 2,
                  name: category?.name,
                  item: `https://c778v.cn/category/${article.category}`,
                },
                {
                  '@type': 'ListItem',
                  position: 3,
                  name: article.title,
                  item: `https://c778v.cn/article/${article.id}`,
                },
              ],
            },
          }),
        }}
      />
    </>
  );
}